<template>
    <div>
    <b-modal
      id="houseareamodal"
      :title="formModal.title"
      @hidden="resetModal"
      @ok="handleSubmit"
      ok-title="Submit"
    >
      <form ref="form" @submit.stop.prevent="handleSubmit" novalidate>

        <b-form-group label="Name" label-for="name-input">
            <b-form-input id="name-input" v-model="form.name" :state="validateState('name')"></b-form-input>
        </b-form-group>

        <b-form-group label="Icon">
            <div ref="iconPicker">
                <button
                    type="button"
                    :class="['btn btn-outline-secondary d-flex align-items-center', $v.form.icon.$dirty && $v.form.icon.$error ? 'border-danger' : '']"
                    @click="showIconPicker = !showIconPicker"
                >
                    <i :class="'las la-' + (form.icon || 'question') + ' la-lg mr-2'"></i>
                    <span>{{ form.icon || 'Select icon…' }}</span>
                    <i :class="'las la-chevron-' + (showIconPicker ? 'up' : 'down') + ' ml-2'"></i>
                </button>
                <div v-if="showIconPicker" class="border rounded mt-1 p-2">
                    <b-form-input v-model="iconSearch" placeholder="Search icons…" size="sm" class="mb-2"></b-form-input>
                    <div style="max-height:280px; overflow-y:auto; display:flex; flex-wrap:wrap; gap:4px;">
                        <button
                            v-for="icon in filteredIcons"
                            :key="icon"
                            type="button"
                            :title="icon"
                            :class="['btn btn-sm p-1', form.icon === icon ? 'btn-primary' : 'btn-outline-secondary']"
                            style="width:42px; height:42px;"
                            @click="selectIcon(icon)"
                        >
                            <i :class="'las la-' + icon"></i>
                        </button>
                    </div>
                    <small class="text-muted mt-1 d-block">{{ filteredIcons.length }} icons</small>
                </div>
                <div v-if="$v.form.icon.$dirty && $v.form.icon.$error" class="text-danger" style="font-size:0.875em; margin-top:0.25rem;">
                    Icon is required.
                </div>
            </div>
        </b-form-group>

        <b-form-group label="Context Helper" label-for="context_helper-input">
            <b-form-textarea id="context_helper-input" v-model="form.context_helper"></b-form-textarea>
        </b-form-group>

      </form>
    </b-modal>

    <b-modal
      id="houseareaimportmodal"
      title="Import House Areas"
      @hidden="resetImport"
      :ok-disabled="importBusy"
      ok-title="Upload"
      @ok="handleImport"
    >
      <div class="mb-3">
        <a :href="route('admin.houses.areas.import.template')" class="btn btn-outline-secondary btn-sm">
          <i class="fas fa-download mr-1"></i> Download Template
        </a>
        <small class="d-block text-muted mt-1">Fill the template then upload it below. Use any icon name from the icon picker (e.g. <code>home</code>, <code>bath</code>).</small>
      </div>

      <b-form-group label="Excel File (.xlsx / .xls / .csv)">
        <b-form-file v-model="importFile" accept=".xlsx,.xls,.csv" :disabled="importBusy"></b-form-file>
      </b-form-group>

      <div v-if="importStatus" class="mt-3">
        <div v-if="importStatus === 'pending' || importStatus === 'processing'" class="d-flex align-items-center text-primary">
          <b-spinner small class="mr-2"></b-spinner>
          Import in progress… ({{ importProcessed }} rows processed)
        </div>
        <div v-else-if="importStatus === 'completed'" class="text-success">
          <i class="fas fa-check-circle mr-1"></i> Import completed — {{ importProcessed }} rows added.
        </div>
        <div v-else-if="importStatus === 'failed'" class="text-danger">
          <i class="fas fa-times-circle mr-1"></i> Import failed: {{ importError }}
        </div>
      </div>
    </b-modal>
    </div>
</template>

<script>
import { required } from "vuelidate/lib/validators";

const ICONS = [
    'home', 'building', 'warehouse', 'store', 'hotel', 'city', 'landmark', 'archway', 'igloo', 'dungeon',
    'door-open', 'bath', 'bed', 'couch', 'toilet', 'shower', 'sink', 'utensils', 'tv', 'desktop', 'fan', 'snowflake',
    'tools', 'wrench', 'hammer', 'screwdriver', 'paint-roller', 'ruler-combined', 'drafting-compass', 'hard-hat', 'paint-brush',
    'bolt', 'fire', 'fire-extinguisher', 'plug', 'tint', 'water', 'thermometer', 'wind', 'solar-panel', 'battery-full', 'lightbulb',
    'key', 'lock', 'lock-open', 'shield', 'shield-alt', 'eye',
    'star', 'heart', 'flag', 'bookmark', 'tag', 'tags', 'search', 'map-marker-alt', 'map', 'compass',
    'layer-group', 'shapes', 'puzzle-piece', 'cog', 'cogs', 'sliders-h', 'th', 'th-large',
    'box', 'boxes', 'archive', 'folder', 'folder-open', 'file', 'file-alt',
    'image', 'camera', 'video', 'phone', 'mobile-alt', 'laptop', 'wifi', 'signal',
    'bell', 'calendar', 'clock', 'stopwatch', 'hourglass-half',
    'check-circle', 'exclamation-triangle', 'info-circle', 'expand', 'compress', 'link', 'palette',
    'user', 'users', 'list', 'list-ul', 'chart-bar', 'chart-pie', 'newspaper', 'book',
    'pen', 'pencil-alt', 'eraser', 'cut', 'copy', 'undo', 'redo', 'history', 'print', 'share-alt', 'upload', 'download',
    'trash', 'trash-alt', 'recycle', 'filter', 'barcode', 'qrcode', 'magic',
    'tree', 'leaf', 'seedling', 'mountain', 'sun', 'cloud', 'umbrella', 'road', 'globe', 'ruler',
    'car', 'truck', 'bicycle', 'walking', 'running', 'swimmer',
    'map-marked-alt', 'location-arrow', 'crosshairs', 'street-view',
];

export default {
    data() {
        return {
            ...this.initData(),
            icons: ICONS,
        };
    },
    computed: {
        filteredIcons() {
            const q = this.iconSearch.toLowerCase();
            return q ? this.icons.filter(i => i.includes(q)) : this.icons;
        },
    },
    watch: {
        showIconPicker(val) {
            if (val) {
                this.$nextTick(() => document.addEventListener('mousedown', this.handleOutsideClick));
            } else {
                document.removeEventListener('mousedown', this.handleOutsideClick);
            }
        },
    },
    beforeDestroy() {
        document.removeEventListener('mousedown', this.handleOutsideClick);
    },
    mounted() {
        // this.openModal();
    },
    validations: {
        form: {
            name: { required },
            icon: { required },
        }
    },
    methods: {
        initData() {
            return {
                form: {
                    name: null,
                    context_helper: null,
                    icon: 'lightbulb',
                },
                formModal: {
                    title: null
                },
                isEdit: false,
                language: 'en',
                iconSearch: '',
                showIconPicker: false,
                importFile: null,
                importBusy: false,
                importStatus: null,
                importProcessed: 0,
                importError: null,
                importPollTimer: null,
            };
        },
        selectIcon(name) {
            this.form.icon = name;
            this.showIconPicker = false;
            this.iconSearch = '';
        },
        handleOutsideClick(e) {
            if (this.$refs.iconPicker && !this.$refs.iconPicker.contains(e.target)) {
                this.showIconPicker = false;
            }
        },
        validateState(name) {
            const { $dirty, $error } = this.$v.form[name];
            return $dirty ? !$error : null;
        },
        openModal(){
            this.isEdit = false;
            this.formModal.title = "Add House Area";
            this.$bvModal.show('houseareamodal');
        },
        async editModal(id){
            this.isEdit = true;
            this.formModal.title = "Edit House Area";
            try {
                const { data } = await axios.get(route("admin.houses.areas.showLang", [id, 'en']));
                this.form = data.data;
                this.$bvModal.show('houseareamodal');
            } catch (e) {
                console.log(e);
                this.$swal('Error!', '', 'error');
            }
            this.$bvModal.show('houseareamodal');
        },
        resetModal() {
            const fresh = this.initData();
            this.form = fresh.form;
            this.iconSearch = fresh.iconSearch;
            this.showIconPicker = fresh.showIconPicker;
            this.$nextTick(() => {
                this.$v.$reset();
            });
        },
        handleSubmit(bvModalEvt){
            bvModalEvt.preventDefault();
            this.$v.form.$touch();
            if (this.$v.form.$anyError) {
                return;
            }
            if(this.isEdit == true){
                this.submitUpdate();
            }else{
                this.submitSave();
            }
        },
        async submitSave(){
            try {
                const { data } = await axios.post(route("admin.houses.areas.store"), {
                    form: this.form,
                    language: this.language
                });
                this.$swal('Saved!', '', 'success');
                this.$bvModal.hide('houseareamodal');
                reloadTable();
            } catch (e) {
                this.$swal('Error!', '', 'error');
            }
        },
        async submitUpdate(){
            try {
                const { data } = await axios.put(route("admin.houses.areas.update", this.form.id), {
                    form: this.form,
                    language: this.language
                });
                this.$swal('Saved!', '', 'success');
                reloadTable();
            } catch (e) {
                console.log(e);
                this.$swal('Error!', '', 'error');
            }
        },
        async deleteUser(id){
             this.$swal({
                title: 'Are you sure?',
                text: "You won't be able to revert this!",
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#3085d6',
                cancelButtonColor: '#d33',
                confirmButtonText: 'Yes, delete it!'
            }).then(async (result) => {
                if (result.isConfirmed) {
                    try {
                        const { data } = await axios.delete(route("admin.houses.areas.destroy", id ));
                        this.$swal({
                            title: 'Deleted!',
                            text: 'House Area has been deleted.',
                            icon: 'success',
                            didClose: () =>{
                                reloadTable();
                            }
                        })
                    } catch (e) {
                        console.log(e);
                    }
                }
            });
        },
        openImportModal() {
            this.$bvModal.show('houseareaimportmodal');
        },
        resetImport() {
            clearInterval(this.importPollTimer);
            this.importFile = null;
            this.importBusy = false;
            this.importStatus = null;
            this.importProcessed = 0;
            this.importError = null;
            this.importPollTimer = null;
        },
        async handleImport(bvModalEvt) {
            bvModalEvt.preventDefault();
            if (!this.importFile) {
                this.$swal('Please select a file.', '', 'warning');
                return;
            }
            this.importBusy = true;
            this.importStatus = 'pending';
            const fd = new FormData();
            fd.append('file', this.importFile);
            try {
                const { data } = await axios.post(route('admin.houses.areas.import'), fd);
                this.pollImportStatus(data.log_id);
            } catch (e) {
                this.importBusy = false;
                this.importStatus = 'failed';
                this.importError = e.response?.data?.message || 'Upload failed.';
            }
        },
        pollImportStatus(logId) {
            this.importPollTimer = setInterval(async () => {
                try {
                    const { data } = await axios.get(route('admin.import-logs.show', logId));
                    this.importStatus = data.status;
                    this.importProcessed = data.processed_rows;
                    if (data.status === 'completed') {
                        clearInterval(this.importPollTimer);
                        this.importBusy = false;
                        reloadTable();
                    } else if (data.status === 'failed') {
                        clearInterval(this.importPollTimer);
                        this.importBusy = false;
                        this.importError = data.error_message;
                    }
                } catch (e) {
                    clearInterval(this.importPollTimer);
                    this.importBusy = false;
                }
            }, 2000);
        }
    }
}

</script>
